/** @jsxImportSource @emotion/react */

import React, { useState, useEffect, useCallback, useRef } from "react";
import OnboardingCTA from "./OnboardingCTA";
import RewardsButton from "./RewardsButton";
import styled from "@emotion/styled";

const LEGAL_TO_CTA_GAP = 10;
const CTA_TO_BOTTOM_GAP = 45;
const NO_CTA_BOTTOM_GAP = 20;

const StickyContainer = styled.div<{ safeAreaPadding: number; hideButton: boolean }>`
  position: fixed;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  background-color: white;
  z-index: 1000;
  box-shadow: 0px -4px 10px rgba(0, 0, 0, 0.05);
  padding-bottom: ${props => Math.max(props.hideButton ? NO_CTA_BOTTOM_GAP : CTA_TO_BOTTOM_GAP, props.safeAreaPadding)}px;
`;

const StickyContent = styled.div`
  width: 90%;
  padding: 16px 0 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;
`;

const LegalContent = styled.div<{ hideButton: boolean }>`
  width: 95%;
  font-size: 14px;
  line-height: 16px;
  margin-bottom: ${props => (props.hideButton ? 0 : LEGAL_TO_CTA_GAP)}px;
`;

const CTAWrapper = styled.div`
  margin-bottom: 0;
  width: 100%;
`;

interface StickyCTAProps {
  legalContent: React.ReactNode;
  ctaText: string;
  ctaAction: () => void;
  loading?: boolean;
  disabled?: boolean;
  onHeightChange?: (height: number) => void;
  hideButton?: boolean;
}

const StickyCTA: React.FC<StickyCTAProps> = ({
  legalContent,
  ctaText,
  ctaAction,
  loading = false,
  disabled = false,
  onHeightChange,
  hideButton = false,
}) => {
  const [safeAreaPadding, setSafeAreaPadding] = useState(CTA_TO_BOTTOM_GAP);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const updateSafeAreaPadding = useCallback(() => {
    const safeAreaBottom = 
      parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0', 10);
    
    setSafeAreaPadding(Math.max(CTA_TO_BOTTOM_GAP, safeAreaBottom));
  }, []);

  const reportHeight = useCallback(() => {
    if (!onHeightChange) {
      return;
    }

    const height = containerRef.current?.getBoundingClientRect().height;
    if (typeof height === "number" && !Number.isNaN(height)) {
      onHeightChange(height);
    }
  }, [onHeightChange]);

  const handleResize = useCallback(() => {
    updateSafeAreaPadding();
    reportHeight();
  }, [reportHeight, updateSafeAreaPadding]);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    reportHeight();
  }, [safeAreaPadding, reportHeight, legalContent]);

  useEffect(() => {
    if (!onHeightChange) {
      return;
    }

    const element = containerRef.current;
    if (!element) {
      return;
    }

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          onHeightChange(entry.contentRect.height);
        }
      });
      observer.observe(element);
      return () => observer.disconnect();
    }

    // Fallback when ResizeObserver is unavailable.
    reportHeight();
  }, [onHeightChange, reportHeight]);

  return (
    <StickyContainer ref={containerRef} safeAreaPadding={safeAreaPadding} hideButton={hideButton}>
      <StickyContent>
        <LegalContent hideButton={hideButton}>{legalContent}</LegalContent>
        {!hideButton && (
          <CTAWrapper>
            <OnboardingCTA>
              <RewardsButton
                clickHandler={ctaAction}
                loading={loading}
                disabled={disabled}
              >
                {ctaText}
              </RewardsButton>
            </OnboardingCTA>
          </CTAWrapper>
        )}
      </StickyContent>
    </StickyContainer>
  );
};

export default StickyCTA;